import React, { useState, useEffect } from 'react';
import {
  Check, Circle, Loader2, ChevronDown, ChevronRight, AlertCircle,
  Home, Wifi, Landmark, Stethoscope, GraduationCap,
  Car, ListChecks, ScrollText,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// iter 184 - Shared inner panel used by both `/portal/checklist` and
// the Blueprint page's Checklist sub-tab. Guarantees the two views are
// visually and behaviourally identical (chevrons, sticky headers,
// reference-item safety, progress math) since they render the same
// component off the same shaped endpoint.

const CATEGORY_META = {
  legal:        { label: 'Legal & Immigration',   icon: ScrollText,     accent: '#B45309' },
  banking:      { label: 'Banking & Finance',     icon: Landmark,       accent: '#0F766E' },
  housing:      { label: 'Housing & Property',    icon: Home,           accent: '#B91C1C' },
  connectivity: { label: 'Connectivity & Setup',  icon: Wifi,           accent: '#7C3AED' },
  healthcare:   { label: 'Healthcare',            icon: Stethoscope,    accent: '#0EA5E9' },
  schools:      { label: 'Schools & Education',   icon: GraduationCap,  accent: '#059669' },
  vehicle:      { label: 'Vehicle & Driving',     icon: Car,            accent: '#EA580C' },
  general:      { label: 'General Tasks',         icon: ListChecks,     accent: '#1A5C6E' },
};

/**
 * ChecklistPanel
 *
 * @param {Object}  props
 * @param {boolean} props.showHeader      - render the "Your action plan · Relocation Checklist"
 *                                          title block. False when embedded in the Blueprint
 *                                          page (which has its own header row).
 * @param {string}  props.emptyMessage    - copy shown when the user has no blueprint yet.
 */
const ChecklistPanel = ({ showHeader = true, emptyMessage = 'Your checklist will appear here once your Blueprint is generated.' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChecklist();
  }, []);

  const fetchChecklist = async () => {
    try {
      setError(null);
      const res = await fetch(`${API_URL}/api/portal/checklist`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
      else setError('Failed to load checklist');
    } catch (err) {
      console.error('[checklist] fetch failed', err);
      setError('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = async (item) => {
    if (item.kind === 'reference') return;
    setToggling(item.id);
    const nextCompleted = !item.completed;
    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((t) => t.id === item.id ? { ...t, completed: nextCompleted } : t);
      const completed = items.filter((t) => t.completed).length;
      const total = items.length;
      const grouped = { ...prev.grouped };
      Object.keys(grouped).forEach((cat) => {
        grouped[cat] = grouped[cat].map((t) => t.id === item.id ? { ...t, completed: nextCompleted } : t);
      });
      return {
        ...prev,
        items, grouped, completed,
        progress_percent: total ? Math.round((completed / total) * 100) : 0,
      };
    });
    try {
      const res = await fetch(`${API_URL}/api/portal/checklist`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ item_id: item.id, completed: nextCompleted }),
      });
      if (!res.ok) {
        await fetchChecklist();
      } else {
        const body = await res.json();
        setData((prev) => prev ? { ...prev, total: body.total, completed: body.completed, progress_percent: body.progress_percent } : prev);
      }
    } catch (err) {
      console.error('[checklist] toggle failed', err);
      await fetchChecklist();
    } finally {
      setToggling(null);
    }
  };

  const toggleExpanded = (taskId) => {
    setExpanded((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" data-testid="checklist-loading" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="text-center py-12 max-w-lg mx-auto">
        <p className="text-[#0A1F2E]/65 mb-4 font-display italic text-xl">{error || emptyMessage}</p>
      </div>
    );
  }

  const { grouped, completed, total, progress_percent, reference_count, checklist_source } = data;
  const emptyState = !grouped || Object.keys(grouped).length === 0;
  // iter 184 - fail-loud banner. When the AI extractor failed twice at
  // Blueprint generation time, we fell back to the legacy regex parser
  // and stamped checklist_source='regex_fallback' on the blueprint row.
  // Surface that here so the user knows their checklist is being
  // regenerated and to expect an update. `admin_alerts` also fires
  // server-side so ops sees the same event.
  const isFallback = checklist_source === 'regex_fallback';

  return (
    <div data-testid="portal-checklist-page">
      {showHeader && (
        <div className="mb-8 sm:mb-10 max-w-4xl mx-auto">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#1A5C6E] font-mono mb-2">
            Your action plan
          </p>
          <h1 className="text-3xl sm:text-4xl font-display italic text-[#0A1F2E] mb-2">
            Relocation Checklist
          </h1>
          <p className="text-[#0A1F2E]/65 text-sm sm:text-base mb-5">
            Track your progress as you complete each step of your relocation.
            {typeof reference_count === 'number' && reference_count > 0 && (
              <> Tap the chevron next to any task to reveal reference data.</>
            )}
          </p>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {isFallback && (
          <div
            className="mb-6 flex items-start gap-3 rounded-2xl border border-[#B45309]/40 bg-[#FEF3C7] px-4 sm:px-5 py-3.5 shadow-sm"
            role="status"
            data-testid="checklist-regenerating-banner"
          >
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#B45309]" />
            <div className="text-sm sm:text-[15px] leading-relaxed">
              <p className="font-medium text-[#0A1F2E]">
                This section is being regenerated - check back shortly.
              </p>
              <p className="text-[#0A1F2E]/70 mt-0.5">
                We hit a hiccup building the personalized version of this
                list. What you see below is our safe fallback and may
                miss a couple of specifics from your Blueprint. Our team
                has been paged automatically; you don&apos;t need to do
                anything.
              </p>
            </div>
          </div>
        )}
        <div
          className="bg-white border border-[#EFE6CF] rounded-2xl p-5 sm:p-6 shadow-sm mb-8"
          data-testid="checklist-progress"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#0A1F2E] font-medium text-sm sm:text-base">Overall Progress</span>
            <span className="font-display italic text-2xl sm:text-3xl text-[#0A1F2E]" data-testid="progress-counter">
              {completed}<span className="text-[#0A1F2E]/40 font-mono text-base ml-1">/ {total}</span>
            </span>
          </div>
          <div className="w-full bg-[#EFE6CF] h-2.5 rounded-full overflow-hidden">
            <div
              className="bg-[#2CA8A0] h-full transition-all duration-500 ease-out rounded-full"
              style={{ width: `${progress_percent}%` }}
              data-testid="progress-bar-fill"
            />
          </div>
          <p className="text-[#1A5C6E] text-xs sm:text-sm mt-2 font-mono" data-testid="progress-percent">
            {progress_percent}% complete
          </p>
        </div>

        {emptyState ? (
          <div className="text-center py-12 bg-white border border-[#EFE6CF] rounded-2xl shadow-sm">
            <Circle className="w-12 h-12 text-[#EFE6CF] mx-auto mb-4" />
            <p className="text-[#0A1F2E]/65 font-display italic text-xl">No checklist items yet</p>
            <p className="text-sm text-[#0A1F2E]/55 mt-2">{emptyMessage}</p>
          </div>
        ) : (
          <div className="space-y-8 sm:space-y-10">
            {Object.entries(grouped).map(([category, tasks]) => {
              const meta = CATEGORY_META[category] || { label: category, icon: ListChecks, accent: '#1A5C6E' };
              const Icon = meta.icon;
              const doneInSection = tasks.filter((t) => t.completed).length;
              return (
                <section
                  key={category}
                  className="bg-white border border-[#EFE6CF] rounded-2xl shadow-sm overflow-hidden"
                  data-testid={`category-${category}`}
                >
                  <div
                    className="sticky top-0 z-10 backdrop-blur-sm bg-[#FCF8EE]/95 border-b-2 px-5 sm:px-7 py-4 flex items-center justify-between"
                    style={{ borderBottomColor: meta.accent }}
                    data-testid={`category-${category}-header`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${meta.accent}18` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: meta.accent }} />
                      </span>
                      <h2
                        className="font-display italic text-xl sm:text-2xl leading-tight"
                        style={{ color: meta.accent }}
                      >
                        {meta.label}
                      </h2>
                    </div>
                    <span className="text-xs sm:text-sm font-mono text-[#0A1F2E]/60">
                      {doneInSection}/{tasks.length}
                    </span>
                  </div>

                  <ul className="divide-y divide-[#EFE6CF]/70">
                    {tasks.map((task) => {
                      const hasRefs = Array.isArray(task.references) && task.references.length > 0;
                      const isExpanded = !!expanded[task.id];
                      const isToggling = toggling === task.id;
                      return (
                        <li key={task.id} data-testid={`task-row-${task.id}`}>
                          <div className="flex items-start gap-3 sm:gap-4 px-5 sm:px-7 py-3.5 sm:py-4 hover:bg-[#FCF8EE]/60 transition-colors">
                            <button
                              type="button"
                              onClick={() => toggleItem(task)}
                              className={`flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-md border-2 flex items-center justify-center transition-all mt-0.5 ${
                                task.completed
                                  ? 'bg-[#2CA8A0] border-[#2CA8A0]'
                                  : 'border-[#0A1F2E]/25 hover:border-[#2CA8A0] bg-white'
                              }`}
                              disabled={isToggling}
                              aria-checked={!!task.completed}
                              role="checkbox"
                              aria-label={task.completed ? 'Mark incomplete' : 'Mark complete'}
                              data-testid={`task-checkbox-${task.id}`}
                            >
                              {isToggling ? (
                                <Loader2 className="w-3 h-3 animate-spin text-[#1A5C6E]" />
                              ) : task.completed ? (
                                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" strokeWidth={3} />
                              ) : null}
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleItem(task)}
                              className={`flex-1 text-left text-sm sm:text-base leading-relaxed cursor-pointer ${
                                task.completed ? 'text-[#0A1F2E]/45 line-through' : 'text-[#0A1F2E]'
                              }`}
                            >
                              {task.text}
                            </button>
                            {hasRefs && (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(task.id)}
                                className="flex-shrink-0 mt-0.5 p-1 rounded hover:bg-[#EFE6CF] text-[#1A5C6E]"
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? 'Hide reference' : 'Show reference'}
                                data-testid={`task-expand-${task.id}`}
                              >
                                {isExpanded
                                  ? <ChevronDown className="w-4 h-4" />
                                  : <ChevronRight className="w-4 h-4" />
                                }
                                <span className="sr-only">Reference ({task.references.length})</span>
                              </button>
                            )}
                          </div>
                          {hasRefs && isExpanded && (
                            <div
                              className="ml-11 sm:ml-14 mr-5 sm:mr-7 mb-4 rounded-xl bg-[#FCF8EE] border border-[#EFE6CF] p-4 sm:p-5"
                              data-testid={`task-references-${task.id}`}
                            >
                              <p className="text-[10px] tracking-[0.15em] uppercase text-[#1A5C6E]/80 font-mono mb-2">
                                Reference · not a task
                              </p>
                              <ul className="space-y-1.5">
                                {task.references.map((r) => (
                                  <li key={r.id} className="text-xs sm:text-sm text-[#0A1F2E]/80 leading-relaxed">
                                    <span className="text-[#1A5C6E] mr-1.5">•</span>{r.text}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistPanel;
